import { Route, Routes } from 'react-router-dom'
import { links, paths } from 'apps'
import { CommonLayout } from 'layouts'
import { PrivateRoute } from 'routes'
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import ConfirmOkModal from 'features/Race/components/ConfirmOkModal'
import { useAppSelector, useViewFiSDK } from 'hooks'
import Loading from 'shared/Loading'
import { TwoLineTitle } from 'shared'
import { getRewardBoosters, claimReward } from 'apis/adApi'
import ItemInformationModal from 'features/Profile/components/ItemInformationModal'
import { useDailyAd } from 'contexts/DailyAdContext'
import { BG_NAME, FX_1, FX_2 } from 'assets/images'

const VIEWFI_PARTNER_ID = process.env.REACT_APP_VIEWFI_PARTNER_ID ?? ''
const VIEWFI_PARTNER_NAME = process.env.REACT_APP_VIEWFI_PARTNER_NAME ?? 'MetaHorse'

/**
 * ViewFi SDK v1.7 Event System
 *
 * Public API: window.ViewFiSDK = { openPopup, closePopup, logout, on, off }
 *
 * Supported events (via SDK .on() or DOM CustomEvent on document):
 *   - adLoaded    { campaignId, title, reward }   — Ad campaign fetched & video ready
 *   - adStarted   { campaignId }                  — Video begins playing
 *   - adCompleted { campaignId, reward }           — Video watched & reward credited
 *   - adClosed    {}                               — Popup/ad closed (≈ pause)
 *   - adError     { type, message }                — Error occurred
 *
 * DOM CustomEvents are prefixed with "viewfi:", e.g. "viewfi:adLoaded".
 */

interface RewardItem {
  name: string
  quantity: number
  img?: string
  booster?: unknown
}

interface ApiRewardItem {
  quantity?: number | string
  name?: string
  booster?: {
    name?: string
    img?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

/** Group rewards by name and sum quantities */
function groupRewards(items: RewardItem[]): RewardItem[] {
  const map = new Map<string, RewardItem>()
  for (const item of items) {
    const existing = map.get(item.name)
    if (existing) {
      existing.quantity += item.quantity
    } else {
      map.set(item.name, { ...item })
    }
  }
  return Array.from(map.values())
}

function WatchAndEarnPage() {
  const auth = useAppSelector(state => state.auth)
  const { hasWatchedAd } = useDailyAd()
  const containerRef = useRef<HTMLDivElement>(null)
  const [showRewardPopup, setShowRewardPopup] = useState(false)
  const [selectedReward, setSelectedReward] = useState<RewardItem | null>(null)
  const [rewards, setRewards] = useState<RewardItem[]>([])
  const groupedRewards = useMemo(() => groupRewards(rewards), [rewards])
  const [remainingViews, setRemainingViews] = useState<number | null>(null)
  const [dailyLimit, setDailyLimit] = useState<number>(0)

  const fetchRewards = useCallback(async () => {
    try {
      const res = await getRewardBoosters()
      const items = Array.isArray(res) ? res : res?.data || res?.items || []

      // Map and filter quantity > 0
      const mappedRewards = items
        .filter((item: ApiRewardItem) => Number(item.quantity) > 0)
        .map((item: ApiRewardItem) => ({
          name: item.booster?.name || item.name || 'Reward',
          quantity: Number(item.quantity),
          img: item.booster?.img,
          booster: item.booster,
        }))

      setRewards(mappedRewards)
    } catch (error) {
      // Failed to fetch rewards — rewards section will be empty
    }
  }, [])

  useEffect(() => {
    fetchRewards()
  }, [fetchRewards])

  // ── ViewFi SDK via shared hook ──────────────────────────────────────
  // SDK is enabled when: logged in and daily ad already watched.
  // Ad loads immediately (non-blocking).
  const sdkEnabled = auth.isLogged && hasWatchedAd === true

  const [timeoutError, setTimeoutError] = useState(false)

  const handleAdError = useCallback(() => {
    setTimeoutError(true)
  }, [])

  const { isProcessing, processingMessage, isSdkReady, getRemainingViews } = useViewFiSDK({
    enabled: sdkEnabled,
    onAdCompleted: () => handleAdCompletedRef.current?.(),
    onRewardPending: undefined,
    onAdError: handleAdError,
    onRewardRejected: undefined,
    onDailyLimitReached: undefined,
  })

  // ── Fetch remaining views once SDK is ready ─────────────────────────
  useEffect(() => {
    if (!isSdkReady) return

    const fetchRemaining = async () => {
      try {
        const result = await getRemainingViews()
        if (!result || result.loggedIn === false) {
          // SDK says not logged in or error — hide remaining text, ad still works
          return
        }
        setRemainingViews(result.remainingViews)
        setDailyLimit(result.dailyLimit)
      } catch {
        // Error fetching — don't block, ad still works
      }
    }

    fetchRemaining()
  }, [isSdkReady, getRemainingViews])

  // ── Ad completed handler (uses ref to avoid circular dep with hook) ──
  const handleAdCompletedRef = useRef<(() => void) | null>(null)
  handleAdCompletedRef.current = async () => {
    try {
      // Claim reward on backend (server-side crediting only)
      await claimReward()
      // Refresh remaining views from SDK
      const result = await getRemainingViews()
      if (result && result.loggedIn !== false) {
        setRemainingViews(result.remainingViews)
        setDailyLimit(result.dailyLimit)
      }
      await fetchRewards()
      setShowRewardPopup(true)
    } catch (error) {
      // Claim failed — still refresh remaining views
      try {
        const result = await getRemainingViews()
        if (result && result.loggedIn !== false) {
          setRemainingViews(result.remainingViews)
          setDailyLimit(result.dailyLimit)
        }
      } catch { /* ignore */ }
    }
  }

  return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4ef076;
          border-radius: 4px;
        }
      `}</style>
      <>
        {/* Heading — TwoLineTitle style (same as MailBox/Home) */}
        <div style={{ fontSize: '32px', lineHeight: '26px', marginBottom: '24px' }}>
          <TwoLineTitle text='Watch Ads & Earn Rewards' />
        </div>

        {/* Reward list with guild-style border */}
        {groupedRewards.length > 0 && (
          <div style={{
            border: '1px solid #47F0B1',
            width: 'fit-content',
            minWidth: 'min(100%, 533px)',
            maxWidth: '975px',
            marginBottom: '28px',
            position: 'relative'
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #121520 -1.15%, #191d2c 100%)',
              padding: '20px 24px',
              position: 'relative'
            }}>
              {/* Bottom left decoration */}
              <div style={{ position: 'absolute', bottom: '-16px', left: 0 }}>
                <img src={FX_1} alt='' />
              </div>
              {/* Bottom right decoration */}
              <div style={{ position: 'absolute', bottom: '-16px', right: 0 }}>
                <img src={FX_2} alt='' />
              </div>
              <div className="custom-scrollbar" style={{
                width: '100%',
                overflowX: 'auto'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateRows: `repeat(${Math.min(groupedRewards.length, 5)}, auto)`,
                  gridAutoFlow: 'column',
                  gap: '12px 24px',
                  width: 'max-content',
                  margin: '0 auto'
                }}>
                {groupedRewards.map((item, idx) => (
                  <div key={idx} onClick={() => setSelectedReward(item)} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#ccc',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'color 0.2s'
                  }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#4ef076')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#ccc')}
                  >
                    {item.img ? <img src={item.img} alt={item.name} style={{ width: '28px', height: '28px', objectFit: 'contain' }} /> : '🎁'}
                    <span style={{ whiteSpace: 'nowrap' }}>{item.name} <span style={{ color: '#4ef076', fontWeight: 600 }}>×{item.quantity}</span></span>
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>

      {/* Login Message or Ad Container */}
      {!auth.isLogged ? (
        <div style={{
          minHeight: '680px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}>
          <div style={{
            fontWeight: 800,
            backgroundColor: '#191d2c',
            padding: '20px',
            color: '#ed842a',
            textAlign: 'center',
            textTransform: 'uppercase',
            width: '100%',
            maxWidth: '840px'
          }}>
            Please login to watch ads and earn rewards.
          </div>
        </div>
      ) : (
        /* SDK auto-initializes from this div */
        <div style={{ width: '100%', maxWidth: '840px', display: 'flex', flexDirection: 'column' }}>
          {remainingViews !== null && dailyLimit > 0 && (
            <div style={{
              alignSelf: 'flex-end',
              marginBottom: '8px',
              padding: '4px 16px 10px',
              backgroundImage: `url(${BG_NAME})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              fontSize: '15px',
              color: '#fff',
              textAlign: 'right'
            }}>
              Remaining Ads: <strong>{Math.max(remainingViews, 0)}</strong>
            </div>
          )}
          <div style={{ flex: 1, width: '100%', display: isProcessing ? 'none' : 'block' }}>
            {sdkEnabled && (
              <div
                ref={containerRef}
                id="w3ap-ad-container"
                data-partner-id={VIEWFI_PARTNER_ID}
                data-partner-name={VIEWFI_PARTNER_NAME}
                data-format="video"
                style={{ width: '100%', height: '100%', minHeight: '680px' }}
              />
            )}
          </div>
          {isProcessing && (
            <div style={{ flex: 1, width: '100%', position: 'relative', minHeight: '680px' }}>
              <Loading content={processingMessage} />
            </div>
          )}
        </div>
      )}

      {/* Reward collection popup */}
      {showRewardPopup && (
        <ConfirmOkModal
          heading='You Got a Reward!'
          onCloseButtonClick={() => setShowRewardPopup(false)}
          isModalWarning
          btnName='Collect'
          width={160}
          isContentCenter
          message={
            <div style={{ minWidth: 'min(100%, 550px)' }}>
              <p>Thanks for watching!</p>
              <p style={{ fontSize: '14px', color: '#aaa' }}>Your reward has been added to your account.</p>
              <div className="custom-scrollbar" style={{ 
                marginTop: '16px', 
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'auto',
                paddingBottom: '8px'
              }}>
                <div style={{
                  display: 'grid', 
                  gridTemplateRows: `repeat(${Math.min(groupedRewards.length, 5)}, auto)`, 
                  gridAutoFlow: 'column', 
                  gap: '8px 24px', 
                  width: 'fit-content',
                  margin: '0 auto'
                }}>
                  {groupedRewards.map((item, idx) => (
                    <div key={idx} onClick={() => setSelectedReward(item)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      {item.img ? <img src={item.img} alt={item.name} style={{ width: '24px', height: '24px', objectFit: 'contain' }} /> : '🎁'}
                      <span style={{ whiteSpace: 'nowrap' }}>{item.name} <span style={{ color: '#4ef076', fontWeight: 600 }}>×{item.quantity}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }
        />
      )}

      {selectedReward && (
        <ItemInformationModal
          open={true}
          onCancel={() => setSelectedReward(null)}
          itemData={selectedReward.booster ? { boost: selectedReward.booster } : undefined}
          onConfirm={() => setSelectedReward(null)}
          isShowBtn={false}
        />
      )}

      {timeoutError && (
        <ConfirmOkModal
          heading='Ad Timeout'
          onCloseButtonClick={() => setTimeoutError(false)}
          isModalWarning
          btnName='OK'
          width={160}
          isContentCenter
          message={
            <div style={{ color: '#fff' }}>
              <p>The ad took too long to respond.</p>
              <p style={{ fontSize: '14px', color: '#aaa' }}>Please try again later.</p>
            </div>
          }
        />
      )}
    </div>
  )
}

function WatchAndEarn() {
  const auth = useAppSelector(state => state.auth)

  return (
    <Routes>
      <Route
        path={paths.default()}
        element={
          <PrivateRoute isLogged={auth.isLogged} layout={CommonLayout} navigate={links.home.index()}>
            <WatchAndEarnPage />
          </PrivateRoute>
        }
      />
    </Routes>
  )
}

export default WatchAndEarn
